import {useRouter} from 'next/router';
import {useTranslation} from 'next-i18next';
import {useCallback, useEffect, useMemo, useRef} from 'react';
import {useSelector} from 'react-redux';

import {RESPONSE_STATUSES} from '@/src/app/constants/api';
import {
  ORDER_BY_FILTER,
  ORDER_FILTER,
  SEARCH_FILTERS,
} from '@/src/app/constants/search';
import {selectResults} from '@/src/app/store/search/searchSlice';
import useSearchActions from '@/src/app/store/search/useSearchActions';
import useUserActions from '@/src/app/store/user/useUserActions';
import transformUserData from '@/src/app/utils/transformUserData';
import useIsMobile from '@/src/hooks/useIsMobile';
import useLoading from '@/src/hooks/useLoading';
import FiltersLayout from '@/src/Search/components/FiltersLayout';
import TotalResultsCount from '@/src/Search/components/TotalResultsCount';
import {getIsQueryParamsExist} from '@/src/Search/containers/SearchContainer';
import SearchFieldContainer from '@/src/Search/containers/SearchFieldContainer';
import SearchResults from '@/src/Search/containers/SearchResults';
import css from '@/src/Search/styles/SearchContainer.module.css';
import {useSearchFilters} from '@/src/Search/utils/useSearchFilters';
import TextLocal from '@/src/textLocal/containers/TextLocal';
import {LoadingWrapper} from '@/src/ui';

import {checkExsistingFilter, getData} from '../utils';

interface ClinicEmployeesSearchProps {
  isAllCandidatesPage: boolean;
}

const ClinicEmployeesSearch = ({
  isAllCandidatesPage,
}: ClinicEmployeesSearchProps) => {
  const {query, locale, isReady: isRouterReady} = useRouter();
  const {isLoading, setLoading} = useLoading();
  const areInitialFiltersSetRef = useRef(false);
  const {isMaxWidth900} = useIsMobile();
  // @ts-ignore
  const {setQueryFilters, isSearchPage} = useSearchFilters();
  const isQueryParamsExist = getIsQueryParamsExist(query);
  const {setResultsDataAction} = useSearchActions();
  const {totalAmount} = useSelector(selectResults);
  const {user} = useUserActions();
  const {t} = useTranslation(TextLocal.LOCALE_SOURCE.COMMON);
  const controllerRef = useRef();

  const initialSearchState = useMemo(() => {
    return {
      [SEARCH_FILTERS.OFFSET]: query[SEARCH_FILTERS.OFFSET] || '0',
      [SEARCH_FILTERS.ORDER]: query[SEARCH_FILTERS.ORDER] || ORDER_FILTER.DESC,
      [SEARCH_FILTERS.ORDER_BY]: ORDER_BY_FILTER.APPROVED_AT,
      [SEARCH_FILTERS.LANGUAGE]: locale,
      ...(isAllCandidatesPage
        ? {[SEARCH_FILTERS.SEARCH]: query[SEARCH_FILTERS.SEARCH] || ''}
        : {}),
    };
  }, [query, locale]);

  useEffect(() => {
    if (isRouterReady && !areInitialFiltersSetRef.current) {
      setQueryFilters({
        ...query,
        ...initialSearchState,
      });

      areInitialFiltersSetRef.current = true;
    }
  }, [isRouterReady, query]);

  useEffect(() => {
    if (controllerRef.current) {
      // @ts-ignore
      controllerRef.current?.abort();
    }

    if (locale === query[SEARCH_FILTERS.LANGUAGE]) {
      // @ts-ignore
      controllerRef.current = new AbortController();

      fetchEmployees(query, query.slug);
    } else {
      setQueryFilters({
        ...query,
        [SEARCH_FILTERS.LANGUAGE]: locale,
      });
    }

    return () => {
      if (controllerRef.current) {
        // @ts-ignore
        controllerRef.current?.abort();
      }

      setResultsDataAction({});
    };
  }, [
    locale,
    query.slug,
    query.search,
    JSON.stringify(checkExsistingFilter(query)),
  ]);

  const fetchEmployees = useCallback(
    async (params, slug) => {
      setLoading(true);

      const {status, responseBody} = await getData(
        params,
        slug,
        null,
        null,
        // @ts-ignore
        controllerRef.current?.signal
      );

      setLoading(false);

      if (status === RESPONSE_STATUSES.SUCCESS) {
        setResultsDataAction({
          data: responseBody.data.map((user) =>
            transformUserData({
              ...user,
              profile: {
                ...user.profile,
                open_for_job: true,
              },
            })
          ),
          totalAmount: responseBody.total_amount,
        });
      }
    },
    [query, isAllCandidatesPage, controllerRef?.current]
  );

  return (
    <>
      <SearchFieldContainer
        withBottomMargin
        searchLabel={t('search.all-employees-in', {
          clinicName: query.clinic_name,
        })}
        withLabel={!isAllCandidatesPage}
      />
      <LoadingWrapper isLoading={isLoading}>
        {isQueryParamsExist && (
          <div className={css.contentWrapper}>
            {isSearchPage && !isLoading && !isMaxWidth900 && (
              <TotalResultsCount
                // @ts-ignore
                activeSearchType={query.slug}
                totalAmount={totalAmount}
                // @ts-ignore
                searchQuery={query[SEARCH_FILTERS.SEARCH]}
              />
            )}
            <div className={css.content}>
              <FiltersLayout />
              {isMaxWidth900 && (
                <TotalResultsCount
                  // @ts-ignore
                  activeSearchType={query.slug}
                  totalAmount={totalAmount}
                  // @ts-ignore
                  searchQuery={query[SEARCH_FILTERS.SEARCH]}
                />
              )}
              <section className={css.section}>
                <SearchResults currentUserId={user?.user?.id} />
              </section>
            </div>
          </div>
        )}
      </LoadingWrapper>
    </>
  );
};

export default ClinicEmployeesSearch;
